import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { backend, type AppUser, type Membership } from '@/services'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AppUser | null>(null)
  const membership = ref<Membership | null>(null)
  const initialized = ref(false)
  const loading = ref(false)
  let unsubscribe: (() => void) | undefined

  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'super_admin')
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin')
  const isAuthenticated = computed(() => Boolean(user.value))

  async function refresh() {
    if (!backend.isConfigured()) {
      user.value = null
      membership.value = null
      initialized.value = true
      return
    }
    try {
      user.value = await backend.getSessionUser()
      membership.value = user.value ? await backend.getMembership() : null
    } finally {
      initialized.value = true
    }
  }

  async function initialize() {
    if (initialized.value) return
    await refresh()
    unsubscribe ||= backend.onAuthChanged(() => void refresh())
  }

  async function signIn(email: string, password: string) {
    loading.value = true
    try {
      await backend.signIn(email, password)
      await refresh()
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    await backend.signOut()
    user.value = null
    membership.value = null
  }

  return {
    user,
    membership,
    initialized,
    loading,
    isAdmin,
    isSuperAdmin,
    isAuthenticated,
    initialize,
    refresh,
    signIn,
    signOut,
  }
})
